import { Controller, Get, Param, Query, UseGuards } from '@nestjs/common';
import { ApiBearerAuth, ApiOperation, ApiSecurity, ApiTags } from '@nestjs/swagger';
import { AdminAccessGuard } from '../auth/admin-access.guard';
import { AuthGuard } from '../auth/auth.guard';
import { AuditQuery, AuditQueryService } from './audit-query.service';
import { AuditRetentionService } from './audit-retention.service';

/**
 * Read-only audit surface for the admin app.
 *
 * Kept out of CommerceAdminController deliberately: that controller's store
 * scoping falls back to the demo store when context is missing, which is a
 * reasonable default for a product list and the wrong one for an audit trail.
 * AuditQueryService fails closed instead.
 */
@ApiTags('Admin · Audit')
@ApiBearerAuth()
@ApiSecurity('store-id')
@Controller('admin/audit')
@UseGuards(AuthGuard, AdminAccessGuard)
export class AuditAdminController {
  constructor(
    private readonly auditQuery: AuditQueryService,
    private readonly retention: AuditRetentionService
  ) {}

  @Get('batches')
  @ApiOperation({ summary: 'Change batches — one row per user action' })
  batches(@Query() query: AuditQuery) {
    return this.auditQuery.listBatches(query);
  }

  @Get('batches/:batchId')
  @ApiOperation({ summary: 'One batch with every record it changed' })
  batch(@Param('batchId') batchId: string) {
    return this.auditQuery.getBatch(batchId);
  }

  @Get('changes')
  @ApiOperation({ summary: 'Flat change log with server-side filters' })
  changes(@Query() query: AuditQuery) {
    return this.auditQuery.listChanges(query);
  }

  @Get('timeline')
  @ApiOperation({ summary: 'Everything that ever happened to one record' })
  timeline(@Query() query: AuditQuery) {
    return this.auditQuery.recordTimeline(query);
  }

  @Get('interface-logs')
  @ApiOperation({ summary: 'Inbound and outbound API calls' })
  interfaceLogs(@Query() query: AuditQuery) {
    return this.auditQuery.listInterfaceLogs(query);
  }

  @Get('interface-logs/:id')
  interfaceLog(@Param('id') id: string) {
    return this.auditQuery.getInterfaceLog(id);
  }

  @Get('activity')
  @ApiOperation({ summary: 'UI activity events' })
  activity(@Query() query: AuditQuery) {
    return this.auditQuery.listActivity(query);
  }

  @Get('correlation/:correlationId')
  @ApiOperation({
    summary: 'One request, its outbound calls, and every row it changed',
  })
  correlation(@Param('correlationId') correlationId: string) {
    return this.auditQuery.correlation(correlationId);
  }

  @Get('facets')
  @ApiOperation({ summary: 'Filter options drawn from the data that exists' })
  facets() {
    return this.auditQuery.facets();
  }

  @Get('stats')
  stats() {
    return this.auditQuery.stats();
  }

  @Get('export/changes')
  @ApiOperation({ summary: 'Filtered change rows, flattened for CSV export' })
  exportChanges(@Query() query: AuditQuery) {
    return this.auditQuery.exportChanges(query);
  }

  @Get('storage')
  @ApiOperation({ summary: 'Audit table sizes — the number that should be watched monthly' })
  async storage() {
    const sizes = await this.retention.sizes();

    // reltuples is an estimate and BigInt does not survive JSON.
    return sizes.map((s) => ({ ...s, bytes: Number(s.bytes), rows: Number(s.rows) }));
  }
}
